import { Component } from '@angular/core'
import { CommonModule } from '@angular/common'
import { DenseScheduleGridComponent } from '../components/dense-schedule-grid/dense-schedule-grid.component'
import { StatusLegendComponent } from '../components/status-legend/status-legend.component'
import { ScheduleDomainService } from '../schedule-domain.service'
import {
  AgendaDateRange,
  AgendaSchedule,
  ScheduleGridResponse,
} from '../components/agenda-calendar/agenda-calendar.models'

/**
 * DENSE_VIEW_MOCK: Standalone preview page for the dense schedule grid.
 * Renders the component with hardcoded fake data so the layout can be inspected
 * without a running backend. Open at /clinic/multi-schedule/dense-preview.
 */
@Component({
  selector: 'app-dense-preview',
  standalone: true,
  imports: [CommonModule, DenseScheduleGridComponent, StatusLegendComponent],
  providers: [ScheduleDomainService],
  template: `
    <div class="dense-preview-host">
      <div class="dense-preview-toolbar">
        <strong>Preview — Densa</strong>
        <div class="dense-preview-tabs">
          <button type="button" [class.active]="mode === 'daily'" (click)="setMode('daily')">Diária</button>
          <button type="button" [class.active]="mode === 'weekly'" (click)="setMode('weekly')">Semanal</button>
        </div>
        <span class="dense-preview-hint">drag-and-drop entre células funciona localmente (sem backend)</span>
      </div>
      <div class="dense-preview-legend">
        <app-status-legend />
      </div>
      <div class="dense-preview-frame">
        <app-dense-schedule-grid
          [dateRange]="dateRange"
          [schedules]="schedules"
          [mockGrids]="mockGrids"
          (eventMove)="onMove($event)"
          (eventMoveBatch)="onMoveBatch($event)"
          (eventClick)="onClick($event)"
          (openAppointmentClick)="onOpenAppointmentClick($event)" />
      </div>
    </div>
  `,
  styles: [`
    :host { display: flex; flex-direction: column; flex: 1; min-height: 100vh; }
    .dense-preview-host { display: flex; flex-direction: column; flex: 1; min-height: 0; background: #f7f6f3; }
    .dense-preview-toolbar {
      display: flex; align-items: center; gap: 1rem; padding: 0.625rem 1rem;
      background: #fff; border-bottom: 1px solid #e5e7eb;
      font-family: 'Inter', sans-serif; font-size: 13px; color: #1c1c1a;
    }
    .dense-preview-tabs { display: inline-flex; border: 1px solid #e5e7eb; border-radius: 7px; overflow: hidden; }
    .dense-preview-tabs button {
      padding: 4px 12px; background: transparent; border: none;
      font-size: 12px; color: #6b7280; cursor: pointer;
    }
    .dense-preview-tabs button.active { background: #5505a2; color: #fff; font-weight: 500; }
    .dense-preview-hint { color: #8a8a8a; font-size: 11px; margin-left: auto; }
    .dense-preview-legend {
      background: #fff; border-bottom: 1px solid #e5e7eb;
      padding-left: 64px; /* aligns with the dense grid time column (--time-col) */
    }
    .dense-preview-frame { flex: 1; min-height: 0; display: flex; }
  `],
})
export class DensePreviewComponent {
  protected mode: 'daily' | 'weekly' = 'daily'
  protected dateRange: AgendaDateRange = (() => {
    const today = new Date().toISOString().split('T')[0]
    return { start: today, end: today }
  })()

  protected readonly schedules: AgendaSchedule[] = [
    { id: 1, name: 'Tomografia Computadorizada de Alta Resolução', color: '#e74c3c' } as AgendaSchedule,
    { id: 2, name: 'Mamografia', color: '#27ae60' } as AgendaSchedule,
    { id: 3, name: 'Densitometria Óssea', color: '#e8a020' } as AgendaSchedule,
    { id: 4, name: 'Angiotomografia Coronária e Estudo Cardiovascular', color: '#3498db' } as AgendaSchedule,
  ]

  protected mockGrids: ScheduleGridResponse[] = []

  private static readonly PATIENT_NAMES = [
    'Maria Aparecida Valentim de Oliveira Figueiredo',
    'Antônio Carlos Bernardino das Neves Magalhães',
    'Francisca Raquel Nascimento de Albuquerque',
    'Teste Animatê de Souza Rodrigues',
    'Conceição Aparecida dos Santos Teixeira Braga',
    'Maria das Graças Evangelista Teixeira Barros',
    'José Expedito Barbosa Menezes da Costa Junior',
    'Luiz Henrique Silveira Guimarães Cavalcante',
    'Pedro Augusto Rodrigues Cavalcante de Melo',
    'Ana Beatriz Figueiredo Albuquerque Nascimento',
    'Teste Animatê TC Torax Sem Contraste',
    'Rafael Augusto Pereira Nascimento Guimarães',
    'Juliana Aparecida Silveira Teixeira dos Santos',
    'Cristina Aparecida Vasconcelos Magalhães Lima',
  ]

  private static readonly PROCEDURES: Record<number, string[]> = {
    1: [
      'TC Tórax de Alta Resolução sem Contraste (HRCT)',
      'TC Abdome Total com Contraste EV — Protocolo Oncológico',
      'TC Coluna Lombar com Reformatações Multiplanares Sagital e Coronal',
      'TC Coluna Cervical com Reconstrução 3D e Reformatação MPR',
      'TC Antebraço Direito com Reconstrução Tridimensional Completa',
    ],
    2: [
      'Mamografia Digital Diagnóstica com Incidências Complementares',
      'Mamografia com Prótese de Silicone Bilateral — Protocolo Eklund',
      'Ultrassom Mamário Bilateral com Mapeamento Doppler Colorido',
      'Tomossíntese Mamária Bilateral com Reconstrução 3D (DBT)',
      'Core Biopsy Estereotáxica Guiada por Mamografia — Mama Esquerda',
    ],
    3: [
      'Densitometria Óssea de Coluna Lombar L1–L4 e Fêmur Bilateral',
      'Densitometria Óssea de Corpo Total com Composição Corporal',
      'Composição Corporal por DEXA — Avaliação de Gordura Visceral',
    ],
    4: [
      'Angiotomografia das Artérias Coronarianas com Reconstrução Multiplanar',
      'Escore de Cálcio Coronariano (Agatston) — Avaliação de Risco',
      'Angiotomografia da Aorta Torácica e Abdominal com Contraste EV',
    ],
  }

  private static readonly STATUSES = [
    { id: 1, name: 'Agendado' },
    { id: 2, name: 'Confirmado' },
    { id: 3, name: 'Em espera' },
    { id: 4, name: 'Atendido' },
    { id: 5, name: 'Faltou' },
  ]

  constructor() {
    this.mockGrids = this.buildMockGrids()
  }

  protected setMode(mode: 'daily' | 'weekly'): void {
    this.mode = mode
    if (mode === 'weekly') {
      const date = new Date(this.dateRange.start + 'T12:00:00')
      const day = date.getDay()
      const start = new Date(date)
      start.setDate(date.getDate() - day)
      const end = new Date(start)
      end.setDate(start.getDate() + 6)
      this.dateRange = {
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0],
      }
    } else {
      const today = new Date().toISOString().split('T')[0]
      this.dateRange = { start: today, end: today }
    }
    this.mockGrids = this.buildMockGrids()
  }

  protected onMove(event: unknown): void {
    // eslint-disable-next-line no-console
    console.log('[dense-preview] eventMove', event)
  }

  protected onMoveBatch(event: unknown): void {
    // eslint-disable-next-line no-console
    console.log('[dense-preview] eventMoveBatch', event)
  }

  protected onClick(event: unknown): void {
    // eslint-disable-next-line no-console
    console.log('[dense-preview] eventClick', event)
  }

  protected onOpenAppointmentClick(event: unknown): void {
    // eslint-disable-next-line no-console
    console.log('[dense-preview] openAppointmentClick', event)
  }

  private buildMockGrids(): ScheduleGridResponse[] {
    const dates = this.expandDates(this.dateRange)
    const grids: ScheduleGridResponse[] = []

    for (const schedule of this.schedules) {
      const days = dates.map((date) => ({
        date,
        slots: this.buildDaySlots(schedule.id, date),
      }))
      grids.push({
        scheduleId: schedule.id,
        scheduleName: schedule.name,
        days,
      })
    }

    return grids
  }

  private expandDates(range: AgendaDateRange): string[] {
    if (range.start === range.end) return [range.start]
    const out: string[] = []
    const cursor = new Date(range.start + 'T12:00:00')
    const end = new Date(range.end + 'T12:00:00')
    while (cursor <= end) {
      out.push(cursor.toISOString().split('T')[0])
      cursor.setDate(cursor.getDate() + 1)
    }
    return out
  }

  private buildDaySlots(scheduleId: number, date: string) {
    const seed = Number(date.replace(/-/g, '')) + scheduleId * 7919
    const rng = (n: number) => {
      const x = Math.sin(seed + n) * 10000
      return x - Math.floor(x)
    }

    type DaySlot = ScheduleGridResponse['days'][number]['slots'][number]
    const slots: DaySlot[] = []
    // Different intervals per schedule to showcase a more populated grid:
    // Tomografia=5, Mamografia=10, Densitometria=15, Coronárias=30
    const interval = scheduleId === 1 ? 5
      : scheduleId === 2 ? 10
      : scheduleId === 3 ? 15
      : scheduleId === 4 ? 30
      : 15
    const showcaseSlots = this.buildShowcaseSlots(scheduleId, date)
    // Skip only the START time of each showcase slot in the random generator, so it doesn't
    // create a conflicting slot at exactly the same start. Intermediate rows inside the
    // showcase appointment's range still get generated normally — the continuation marker
    // will overlay them automatically in buildView.
    const showcaseTimes = new Set<string>(
      showcaseSlots.map((s: DaySlot) => s.startDateTime.substring(11, 16)),
    )
    slots.push(...showcaseSlots)

    let h = 7
    let m = 0
    let i = 0
    // Unique per (scheduleId, date) so IDs don't collide across days/weeks.
    const appointmentIdSeed = (scheduleId * 1_000_000) + Number(date.replace(/-/g, '')) * 1000

    // Working hours: 07:00 → 19:00, with a 12:00 → 13:00 lunch break (no slots generated).
    while (h < 19 || (h === 19 && m === 0)) {
      const startTime = this.fmt(h, m)
      const nextM = m + interval
      const endH = nextM >= 60 ? h + 1 : h
      const endM = nextM >= 60 ? nextM - 60 : nextM
      const endTime = this.fmt(endH, endM)

      const startDateTime = `${date}T${startTime}:00`
      const endDateTime = `${date}T${endTime}:00`

      // Lunch break: any slot whose start is between 12:00 (inclusive) and 13:00 (exclusive)
      // is skipped — the schedule simply has no row in that window.
      if (h === 12) {
        m = nextM
        if (m >= 60) { h++; m -= 60 }
        i++
        continue
      }

      if (showcaseTimes.has(startTime)) {
        m = nextM
        if (m >= 60) { h++; m -= 60 }
        i++
        continue
      }

      const r = rng(i)
      if (r < 0.55) {
        const status = DensePreviewComponent.STATUSES[Math.floor(rng(i + 100) * DensePreviewComponent.STATUSES.length)]
        const patient = DensePreviewComponent.PATIENT_NAMES[Math.floor(rng(i + 200) * DensePreviewComponent.PATIENT_NAMES.length)]
        const procs = DensePreviewComponent.PROCEDURES[scheduleId] ?? []
        const procName = procs[Math.floor(rng(i + 300) * procs.length)] ?? 'Procedimento'

        slots.push({
          startDateTime,
          endDateTime,
          type: 'appointment',
          slotId: appointmentIdSeed + i,
          appointment: {
            id: appointmentIdSeed + i,
            isExtraSlot: false,
            status,
            patient: { id: 1000 + i, name: patient },
            procedures: [{ id: 500 + i, name: procName }],
            insurance: { id: 1, name: 'Unimed Nacional' },
          },
        })
      } else if (r < 0.92) {
        slots.push({
          startDateTime,
          endDateTime,
          type: 'free',
          slotId: appointmentIdSeed + i,
        })
      } else {
        slots.push({
          startDateTime,
          endDateTime,
          type: 'blocked',
          slotId: appointmentIdSeed + i,
          block: { id: i, observation: 'Bloqueio' },
        })
      }

      m = nextM
      if (m >= 60) {
        h++
        m -= 60
      }
      i++
    }

    return slots
  }

  private fmt(h: number, m: number): string {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }

  // Showcase scenarios: injected for the FIRST date of the range, on specific schedules.
  private buildShowcaseSlots(scheduleId: number, date: string) {
    type DaySlot = ScheduleGridResponse['days'][number]['slots'][number]
    if (date !== this.dateRange.start) return [] as DaySlot[]

    // (0) Tomografia (id=1):
    //     - 07:00 → 07:30: 30-min appointment that crosses multiple template rows
    //     - 07:01 / 07:04 / 07:06: short appointments sharing buckets 07:00 and 07:05
    if (scheduleId === 1) {
      return [
        {
          startDateTime: `${date}T07:00:00`,
          endDateTime: `${date}T07:30:00`,
          type: 'appointment',
          slotId: 8001,
          appointment: {
            id: 8001,
            isExtraSlot: false,
            status: { id: 2, name: 'Confirmado' },
            patient: { id: 8001, name: 'Tereza Cristina Fonseca Magalhães de Andrade' },
            procedures: [{ id: 100, name: 'TC Tórax de Alta Resolução com Reconstrução Multiplanar' }],
            insurance: { id: 1, name: 'Unimed Nacional' },
          },
        },
        {
          startDateTime: `${date}T07:01:00`,
          endDateTime: `${date}T07:11:00`,
          type: 'appointment',
          slotId: 8002,
          appointment: {
            id: 8002,
            isExtraSlot: true,
            status: { id: 1, name: 'Agendado' },
            patient: { id: 8002, name: 'Eduardo Ramos Benedito Fonseca de Almeida' },
            procedures: [{ id: 101, name: 'TC Crânio Simples sem Contraste' }],
            insurance: { id: 2, name: 'Bradesco Saúde' },
          },
        },
        {
          startDateTime: `${date}T07:04:00`,
          endDateTime: `${date}T07:14:00`,
          type: 'appointment',
          slotId: 8003,
          appointment: {
            id: 8003,
            isExtraSlot: true,
            status: { id: 3, name: 'Em espera' },
            patient: { id: 8003, name: 'Vanessa de Fátima Rodrigues Albuquerque Costa' },
            procedures: [{ id: 102, name: 'TC Pelve sem Contraste' }],
            insurance: { id: 3, name: 'SulAmérica Saúde' },
          },
        },
        {
          startDateTime: `${date}T07:06:00`,
          endDateTime: `${date}T07:16:00`,
          type: 'appointment',
          slotId: 8004,
          appointment: {
            id: 8004,
            isExtraSlot: true,
            status: { id: 2, name: 'Confirmado' },
            patient: { id: 8004, name: 'Marcos Antônio Barbosa Rodrigues Cavalcante' },
            procedures: [{ id: 103, name: 'TC Coluna Lombar sem Contraste' }],
            insurance: { id: 1, name: 'Unimed Nacional' },
          },
        },
      ] as DaySlot[]
    }

    if (scheduleId !== 2) return [] as DaySlot[]

    const slots: DaySlot[] = []
    const idBase = 9000

    // (0a) Bucket 07:00 — three appointments for the SAME patient at 07:00 / 07:01 / 07:02
    //      (e.g. patient scheduled for multiple sequential exams in the same arrival).
    const samePatient = { id: idBase + 100, name: 'Isabela Cristine Figueiredo Magalhães Barros' }
    const samePatientInsurance = { id: 1, name: 'Unimed Nacional' }
    slots.push({
      startDateTime: `${date}T07:00:00`,
      endDateTime: `${date}T07:10:00`,
      type: 'appointment',
      slotId: idBase + 101,
      appointment: {
        id: idBase + 101,
        isExtraSlot: false,
        status: { id: 2, name: 'Confirmado' },
        patient: samePatient,
        procedures: [{ id: 200, name: 'Mamografia Digital Bilateral' }],
        insurance: samePatientInsurance,
      },
    })
    slots.push({
      startDateTime: `${date}T07:01:00`,
      endDateTime: `${date}T07:11:00`,
      type: 'appointment',
      slotId: idBase + 102,
      appointment: {
        id: idBase + 102,
        isExtraSlot: true,
        status: { id: 2, name: 'Confirmado' },
        patient: samePatient,
        procedures: [{ id: 201, name: 'Ultrassom Mamário Complementar' }],
        insurance: samePatientInsurance,
      },
    })
    slots.push({
      startDateTime: `${date}T07:02:00`,
      endDateTime: `${date}T07:12:00`,
      type: 'appointment',
      slotId: idBase + 103,
      appointment: {
        id: idBase + 103,
        isExtraSlot: true,
        status: { id: 2, name: 'Confirmado' },
        patient: samePatient,
        procedures: [{ id: 202, name: 'Tomossíntese Mamária 3D (DBT)' }],
        insurance: samePatientInsurance,
      },
    })

    // (0b) Bucket 07:05 — three appointments for DIFFERENT patients at 07:05 / 07:06 / 07:07
    slots.push({
      startDateTime: `${date}T07:05:00`,
      endDateTime: `${date}T07:15:00`,
      type: 'appointment',
      slotId: idBase + 104,
      appointment: {
        id: idBase + 104,
        isExtraSlot: false,
        status: { id: 1, name: 'Agendado' },
        patient: { id: idBase + 104, name: 'Cristina Aparecida Vasconcelos Magalhães Lima' },
        procedures: [{ id: 203, name: 'Mamografia Diagnóstica com Magnificação' }],
        insurance: { id: 4, name: 'Amil Saúde' },
      },
    })
    slots.push({
      startDateTime: `${date}T07:06:00`,
      endDateTime: `${date}T07:16:00`,
      type: 'appointment',
      slotId: idBase + 105,
      appointment: {
        id: idBase + 105,
        isExtraSlot: true,
        status: { id: 3, name: 'Em espera' },
        patient: { id: idBase + 105, name: 'Luciana Conceição Evangelista Vasconcelos Neto' },
        procedures: [{ id: 204, name: 'Core Biopsy Estereotáxica Guiada por Mamografia' }],
        insurance: { id: 1, name: 'Unimed Nacional' },
      },
    })
    slots.push({
      startDateTime: `${date}T07:07:00`,
      endDateTime: `${date}T07:17:00`,
      type: 'appointment',
      slotId: idBase + 106,
      appointment: {
        id: idBase + 106,
        isExtraSlot: true,
        status: { id: 2, name: 'Confirmado' },
        patient: { id: idBase + 106, name: 'Rodrigo Henrique Cavalcante dos Santos Melo' },
        procedures: [{ id: 205, name: 'Punção Aspirativa por Agulha Fina (PAAF) Guiada' }],
        insurance: { id: 3, name: 'SulAmérica Saúde' },
      },
    })

    // (1) 08:00 — two appointments in the same time block
    slots.push({
      startDateTime: `${date}T08:00:00`,
      endDateTime: `${date}T08:20:00`,
      type: 'appointment',
      slotId: idBase + 1,
      appointment: {
        id: idBase + 1,
        isExtraSlot: false,
        status: { id: 2, name: 'Confirmado' },
        patient: { id: idBase + 1, name: 'Maria Aparecida Valentim de Oliveira Figueiredo' },
        procedures: [{ id: 1, name: 'Mamografia Digital Bilateral — Rastreamento Anual' }],
        insurance: { id: 1, name: 'Unimed Nacional' },
      },
    })
    slots.push({
      startDateTime: `${date}T08:00:00`,
      endDateTime: `${date}T08:20:00`,
      type: 'appointment',
      slotId: idBase + 2,
      appointment: {
        id: idBase + 2,
        isExtraSlot: true,
        status: { id: 1, name: 'Agendado' },
        patient: { id: idBase + 2, name: 'Antônio Carlos Bernardino das Neves Magalhães' },
        procedures: [{ id: 2, name: 'Ultrassom Mamário Bilateral com Mapeamento Doppler Colorido' }],
        insurance: { id: 2, name: 'Bradesco Saúde' },
      },
    })

    // (2) 09:00 — one appointment + one blocked in the same time block
    slots.push({
      startDateTime: `${date}T09:00:00`,
      endDateTime: `${date}T09:20:00`,
      type: 'appointment',
      slotId: idBase + 3,
      appointment: {
        id: idBase + 3,
        isExtraSlot: false,
        status: { id: 3, name: 'Em espera' },
        patient: { id: idBase + 3, name: 'Francisca Raquel Nascimento de Albuquerque' },
        procedures: [{ id: 3, name: 'Tomossíntese Mamária Bilateral com Reconstrução 3D (DBT)' }],
        insurance: { id: 1, name: 'Unimed Nacional' },
      },
    })
    slots.push({
      startDateTime: `${date}T09:00:00`,
      endDateTime: `${date}T09:20:00`,
      type: 'blocked',
      slotId: idBase + 4,
      block: { id: idBase + 4, observation: 'Manutenção do equipamento' },
    })

    // (3) 10:00 — one appointment with multiple procedures
    slots.push({
      startDateTime: `${date}T10:00:00`,
      endDateTime: `${date}T10:20:00`,
      type: 'appointment',
      slotId: idBase + 5,
      appointment: {
        id: idBase + 5,
        isExtraSlot: false,
        status: { id: 2, name: 'Confirmado' },
        patient: { id: idBase + 5, name: 'Conceição Aparecida dos Santos Teixeira Braga' },
        procedures: [
          { id: 10, name: 'Mamografia Digital Bilateral — Rastreamento Anual' },
          { id: 11, name: 'Ultrassom Mamário Bilateral com Mapeamento Doppler' },
          { id: 12, name: 'Tomossíntese Mamária Bilateral 3D (DBT)' },
        ],
        insurance: { id: 1, name: 'Unimed Nacional' },
      },
    })

    // (4) 11:00 — two appointments 1 minute apart (11:00 and 11:01)
    slots.push({
      startDateTime: `${date}T11:00:00`,
      endDateTime: `${date}T11:10:00`,
      type: 'appointment',
      slotId: idBase + 6,
      appointment: {
        id: idBase + 6,
        isExtraSlot: false,
        status: { id: 2, name: 'Confirmado' },
        patient: { id: idBase + 6, name: 'Tereza Cristina Fonseca Magalhães de Andrade' },
        procedures: [{ id: 20, name: 'Mamografia Digital Diagnóstica Bilateral' }],
        insurance: { id: 1, name: 'Unimed Nacional' },
      },
    })
    slots.push({
      startDateTime: `${date}T11:01:00`,
      endDateTime: `${date}T11:11:00`,
      type: 'appointment',
      slotId: idBase + 7,
      appointment: {
        id: idBase + 7,
        isExtraSlot: true,
        status: { id: 1, name: 'Agendado' },
        patient: { id: idBase + 7, name: 'Sebastiana Rodrigues Ferreira da Conceição' },
        procedures: [{ id: 21, name: 'Ultrassom Mamário Complementar (PAAF)' }],
        insurance: { id: 3, name: 'SulAmérica Saúde' },
      },
    })

    // (5) 11:20 — appointment + blocked sharing the same time block
    slots.push({
      startDateTime: `${date}T11:20:00`,
      endDateTime: `${date}T11:40:00`,
      type: 'appointment',
      slotId: idBase + 8,
      appointment: {
        id: idBase + 8,
        isExtraSlot: false,
        status: { id: 4, name: 'Atendido' },
        patient: { id: idBase + 8, name: 'Benedito Alexandre Gonçalves Vasconcelos Filho' },
        procedures: [{ id: 22, name: 'Core Biopsy Estereotáxica Guiada por Mamografia' }],
        insurance: { id: 1, name: 'Unimed Nacional' },
      },
    })
    slots.push({
      startDateTime: `${date}T11:20:00`,
      endDateTime: `${date}T11:40:00`,
      type: 'blocked',
      slotId: idBase + 9,
      block: { id: idBase + 9, observation: 'Limpeza da sala' },
    })

    // (6) 10:20 → 11:00 — long appointment (40min) crossing two template rows.
    //     10:40 also has a short appointment (10min) — its cell will show the short card
    //     plus a "continuation" marker from the long appointment that started at 10:20.
    slots.push({
      startDateTime: `${date}T10:20:00`,
      endDateTime: `${date}T11:00:00`,
      type: 'appointment',
      slotId: idBase + 10,
      appointment: {
        id: idBase + 10,
        isExtraSlot: false,
        status: { id: 2, name: 'Confirmado' },
        patient: { id: idBase + 10, name: 'Raimundo Nonato Albuquerque Cavalcanti Neto' },
        procedures: [{ id: 30, name: 'Tomossíntese Mamária com Reconstrução Volumétrica e Biópsia Guiada' }],
        insurance: { id: 1, name: 'Unimed Nacional' },
      },
    })
    slots.push({
      startDateTime: `${date}T10:40:00`,
      endDateTime: `${date}T10:50:00`,
      type: 'appointment',
      slotId: idBase + 11,
      appointment: {
        id: idBase + 11,
        isExtraSlot: true,
        status: { id: 1, name: 'Agendado' },
        patient: { id: idBase + 11, name: 'Vanessa de Fátima Rodrigues Albuquerque Costa' },
        procedures: [{ id: 31, name: 'Mamografia de Magnificação Localizada' }],
        insurance: { id: 4, name: 'Amil Saúde' },
      },
    })

    return slots
  }
}
